import React, { useEffect, useRef } from "react";
import { SearchCheck, User, UserSearch, MousePointerClick } from "lucide-react";

function TutorComponent() {
  const headingRef = useRef(null);
  const subRef = useRef(null);
  const btnsRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    const els = [headingRef, subRef, btnsRef, imgRef];
    els.forEach((ref, i) => {
      if (ref.current) {
        ref.current.style.opacity = "0";
        ref.current.style.transform = "translateY(32px)";
        setTimeout(
          () => {
            if (ref.current) {
              ref.current.style.transition =
                "opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)";
              ref.current.style.opacity = "1";
              ref.current.style.transform = "translateY(0)";
            }
          },
          120 * i + 80,
        );
      }
    });
  }, []);

  return (
    <section className="relative w-full overflow-hidden bg-[#0a0f1e] min-h-screen flex items-center">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Radial glow top-left */}
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#25d976] opacity-[0.07] blur-[120px]" />
        {/* Radial glow bottom-right */}
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#3b82f6] opacity-[0.09] blur-[100px]" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 py-16 lg:py-0">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8 lg:min-h-screen lg:py-24">
          {/* ── Left: Text Content ── */}
          <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
            {/* Badge */}
            <div
              ref={headingRef}
              className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm"
            >
              <span className="w-2 h-2 rounded-full bg-[#25d976] animate-pulse" />
              <span className="text-[#25d976] text-xs font-semibold tracking-widest uppercase font-mono">
                বাংলাদেশের #১ নেটওয়ার্ক
              </span>
            </div>

            {/* Heading */}
            <h2
              ref={headingRef}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.1] tracking-tight mb-5"
              style={{ fontFamily: "'Hind Siliguri', sans-serif" }}
            >
              <span className="relative inline-block">
                <span className="relative z-10 text-[#25d976]">টিউটর</span>{" "}
                নেটওয়ার্ক
                <span
                  className="absolute bottom-1 left-0 w-full h-[6px] bg-[#25d976] rounded-full opacity-40"
                  style={{ filter: "blur(2px)" }}
                />
              </span>
            </h2>

            {/* Sub */}
            <p
              ref={subRef}
              className="text-white/55 text-base sm:text-lg lg:text-xl leading-relaxed mb-10 max-w-md mx-auto lg:mx-0"
              style={{ fontFamily: "'Hind Siliguri', sans-serif" }}
            >
              Find the right home tutor—or get hired as one.
              <br />
              Join Tutor Network today.
            </p>

            {/* Buttons */}
            <div
              ref={btnsRef}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              {/* Primary CTA */}
              <a
                href="/tutor-submission"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center gap-2.5 bg-[#25d976] hover:bg-[#f5ffaa] text-[#0a0f1e] font-bold text-sm sm:text-base px-7 py-4 rounded-2xl transition-all duration-300 shadow-[0_0_30px_rgba(232,255,71,0.25)] hover:shadow-[0_0_50px_rgba(232,255,71,0.45)] hover:-translate-y-0.5 overflow-hidden"
              >
                <MousePointerClick />
                <span
                  className="relative z-10"
                  style={{ fontFamily: "'Hind Siliguri', sans-serif" }}
                >
                  Become a Tutor
                </span>
              </a>

              {/* Secondary CTA */}
              <a
                href="/post-tuition"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 hover:border-white/30 text-white font-semibold text-sm sm:text-base px-7 py-4 rounded-2xl transition-all duration-300 hover:-translate-y-0.5 backdrop-blur-sm"
              >
                <i className="fa-solid fa-circle-plus" aria-hidden="true" />

                <span style={{ fontFamily: "'Hind Siliguri', sans-serif" }}>
                  Post a Tuition
                </span>
              </a>
            </div>

            {/* Stats strip */}
            <div className="mt-12 flex items-center justify-center lg:justify-start gap-8 flex-wrap">
              {[
                { num: "10K+", label: "শিক্ষার্থী" },
                { num: "500+", label: "টিউটর" },
                { num: "৪.৯★", label: "রেটিং" },
              ].map((s) => (
                <div key={s.label} className="text-center lg:text-left">
                  <p className="text-white font-black text-xl sm:text-2xl leading-none">
                    {s.num}
                  </p>
                  <p
                    className="text-white/40 text-xs mt-0.5"
                    style={{ fontFamily: "'Hind Siliguri', sans-serif" }}
                  >
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Image ── */}
          <div
            ref={imgRef}
            className="flex-1 order-1 lg:order-2 flex items-center justify-center lg:justify-end w-full max-w-[340px] sm:max-w-[420px] lg:max-w-none mx-auto"
          >
            <div className="relative">
              {/* Glow ring behind image */}
              <div className="absolute inset-[-20px] rounded-[40px] bg-gradient-to-br from-[#e8ff47]/20 via-transparent to-[#3b82f6]/20 blur-2xl" />

              {/* Card frame */}
              <div className="relative rounded-[32px] overflow-hidden border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.6)]">
                <img
                  src="https://goatteachers.com/assets/billboard.webp"
                  alt="বাংলাদেশে প্রাইভেট টিউশন ও হোম টিউটর খোঁজার Goat Teachers Tutor Network"
                  className="w-full h-full object-cover block"
                  style={{ maxHeight: "520px" }}
                  loading="lazy"
                />
                {/* Subtle overlay gradient on image bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a0f1e]/60 to-transparent" />

                {/* Floating chip */}
                {/* <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-transparent border border-white/10 px-4 py-2 rounded-full whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#25c13f] animate-pulse" />
                  <span className="text-white text-xs font-semibold tracking-wide">
                    Live Tutors Available
                  </span>
                </div> */}
              </div>

              {/* Decorative corner dots */}
              <div
                className="absolute -top-4 -right-4 w-20 h-20 opacity-30"
                style={{
                  backgroundImage:
                    "radial-gradient(circle, #e8ff47 1.5px, transparent 1.5px)",
                  backgroundSize: "8px 8px",
                }}
              />
              <div
                className="absolute -bottom-4 -left-4 w-16 h-16 opacity-20"
                style={{
                  backgroundImage:
                    "radial-gradient(circle, #3b82f6 1.5px, transparent 1.5px)",
                  backgroundSize: "8px 8px",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Google Font import via style tag */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;500;600;700&display=swap');
      `}</style>
    </section>
  );
}

export default TutorComponent;
